import { toast } from "react-toastify";
import newApi from "@services/newApi/api";
import newApi_public from "@services/newApi/api_public";
import new_api_public from "@services/newApi/api_public";
import type {
  ICompletePatientRegistrationParams,
  ICompletePatientRegistrationResponse,
  IConvenio,
  ICreateDoctorParams,
  ICreateDoctorResponse,
  ICreateUserConvenioParams,
  ICreateUserConvenioResponse,
  ICreateUserHospitalParams,
  ICreateUserHospitalResponse,
  IPatientRegistrationLookupParams,
  IPatientRegistrationLookupResponse,
  ISendPatientValidationCodeParams,
  ISendPatientValidationCodeResponse,
  ISpeciality,
  IValidatePatientValidationCodeParams,
  IValidatePatientValidationCodeResponse,
} from "./types";
import type { AxiosResponse } from "axios";
import type { IHospital } from "@features/hospitals";

export function createRegisterUtils() {
  return {
    async createDoctor(data: ICreateDoctorParams) {
      try {
        const response = await newApi_public.post<ICreateDoctorResponse>(
          `/register/doctor`,
          data,
        );
        if (response?.data?.message) {
          toast.success(response?.data?.message);
        }
        return response;
      } catch (error: any) {
        console.error((error as any)?.message);
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        }
      }
    },

    async createUserHospital(
      data: ICreateUserHospitalParams,
    ): Promise<AxiosResponse<ICreateUserHospitalResponse, any>> {
      try {
    const response = await newApi_public.post<ICreateUserHospitalResponse>(
      `register/hospital/user`,
      data,
    );
        return response;
      } catch (error: any) {
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        }
        return error;
      }
    },

    async speciality() {
      try {
        // api_public já auto-unwrap envelope — response.data é o inner.
        const response = await newApi_public.get<ISpeciality[] | { specialities?: ISpeciality[] }>(
          `/register/doctor/speciality`,
        );
        const data: any = response.data;
        // Tolera retorno como array direto OU objeto { specialities: [] }.
        return Array.isArray(data) ? data : (data?.specialities ?? data?.items ?? []);
      } catch (error: any) {
        // intencional silencioso — endpoint é nice-to-have no flow de cadastro
      }
    },

    async hospitalsList() {
      try {
        // newApi (authenticated) NÃO faz auto-unwrap; tem que ler `.data.data`.
        const response = await newApi.get<{ data: { hospitals: IHospital[] } }>(
          "register/hospitals/list",
        );
        const hospitals =
          response.data?.data?.hospitals ??
          (response.data as any)?.hospitals ??
          [];
        return { data: hospitals };
      } catch (error: any) {
        // intencional silencioso
      }
    },

    async allHospitalsButAmbList() {
      try {
        const response = await newApi.get<{ data: { hospitals: IHospital[] } }>(
          "register/hospitals/list",
        );
        const hospitals =
          response.data?.data?.hospitals ??
          (response.data as any)?.hospitals ??
          [];
        return { data: hospitals };
      } catch (error: any) {
        // intencional silencioso
      }
    },

    async convenioList() {
      return await new_api_public.get<IConvenio[]>(`/health-insurance`, { params: { type: 6 } });
    },

    async createUserConvenio(
      data: ICreateUserConvenioParams,
    ): Promise<AxiosResponse<ICreateUserConvenioResponse, any> | undefined> {
      try {
        const response = await newApi_public.post<ICreateUserConvenioResponse>(
          `user/convenio/register`,
          data,
        );
        return response;
      } catch (error: any) {
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        }
        return undefined;
      }
    },

    async searchPatientRegistration(data: IPatientRegistrationLookupParams) {
      try {
        const response =
          await new_api_public.post<IPatientRegistrationLookupResponse>(
            "/patient/register/lookup",
            data,
          );
        return response;
      } catch (error: any) {
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        }
      }
    },

    async completePatientRegistration(data: ICompletePatientRegistrationParams) {
      try {
        const response =
          await new_api_public.post<ICompletePatientRegistrationResponse>(
            "/patient/register",
            data,
          );
        if (response?.data?.message) {
          toast.success(response?.data?.message);
        }
        return response;
      } catch (error: any) {
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        }
      }
    },

    async sendPatientValidationCode(data: ISendPatientValidationCodeParams) {
      try {
        const response =
          await new_api_public.post<ISendPatientValidationCodeResponse>(
            "/patient/validation/send-code",
            data,
          );
        if (response?.data?.message) {
          toast.success(response?.data?.message);
        }
        return response;
      } catch (error: any) {
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        }
      }
    },

    async validatePatientValidationCode(
      data: IValidatePatientValidationCodeParams,
    ) {
      try {
        const response =
          await new_api_public.post<IValidatePatientValidationCodeResponse>(
            "/auth/validate/code",
            data,
          );
        if (response?.data?.message) {
          toast.success(response?.data?.message);
        }
        return response;
      } catch (error: any) {
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        }
      }
    },
  };
}
